import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { FormField, FormItem, FormLabel, FormMessage } from '../ui/form';
import { Alert, AlertDescription } from '../ui/alert';
import { api, Category, UpdateCategoryDto } from '../../services/api';

const categorySchema = z.object({
  name: z.string()
    .min(1, 'Name is required')
    .max(100, 'Name must be less than 100 characters')
    .regex(/^[a-zA-Z0-9\s\-_]+$/, 'Name can only contain letters, numbers, spaces, hyphens, and underscores'),
  description: z.string()
    .max(1000, 'Description must be less than 1000 characters')
    .optional(),
  color: z.string()
    .regex(/^#[0-9A-Fa-f]{6}$/, 'Color must be a valid hex color (e.g., #FF5733)')
});

type CategoryFormData = z.infer<typeof categorySchema>;

interface CategoryEditDialogProps {
  category: Category | null; // null for create mode
  open: boolean;
  onClose: () => void;
  onSave: (category: Category) => void;
}

const DEFAULT_COLORS = [
  '#FF5733', '#33FF57', '#3357FF', '#FF33F5', '#F5FF33',
  '#33FFF5', '#FF8C33', '#8C33FF', '#33FF8C', '#F533FF'
];

export const CategoryEditDialog: React.FC<CategoryEditDialogProps> = ({
  category,
  open,
  onClose,
  onSave
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [nameCheckLoading, setNameCheckLoading] = useState(false);

  const isEditMode = !!category?.id;

  const form = useForm<CategoryFormData>({
    resolver: zodResolver(categorySchema),
    defaultValues: {
      name: '',
      description: '',
      color: DEFAULT_COLORS[0]
    }
  });

  // Reset form when category changes
  useEffect(() => {
    if (category) {
      form.reset({
        name: category.name || '',
        description: category.description || '',
        color: category.color || DEFAULT_COLORS[0]
      });
    } else {
      form.reset({
        name: '',
        description: '',
        color: DEFAULT_COLORS[0]
      });
    }
    setError(null);
    setValidationErrors([]);
  }, [category, form]);

  // Real-time color validation
  const watchedColor = form.watch('color');
  const isValidHexColor = /^#[0-9A-Fa-f]{6}$/.test(watchedColor || '');

  // Real-time name validation
  const watchedName = form.watch('name');
  useEffect(() => {
    if (!watchedName || watchedName.length < 2) {
      setValidationErrors([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        setNameCheckLoading(true);
        const result = await api.validateCategoryName(
          watchedName, 
          isEditMode ? category!.id : undefined
        );
        setValidationErrors(result.errors);
      } catch (err) {
        console.error('Name validation error:', err);
      } finally {
        setNameCheckLoading(false);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [watchedName, isEditMode, category]);

  const handleSubmit = async (data: CategoryFormData) => {
    try {
      setLoading(true);
      setError(null);

      if (isEditMode) {
        // Update existing category
        const updateData: UpdateCategoryDto = {
          name: data.name,
          description: data.description,
          color: data.color,
          rowVersion: category!.rowVersion!
        };

        const updatedCategory = await api.updateCategory(category!.id, updateData);
        onSave(updatedCategory);
      } else {
        // Create new category
        const newCategory = await api.createCategory({
          name: data.name,
          description: data.description,
          color: data.color
        });
        onSave(newCategory);
      }
    } catch (err) {
      if (err instanceof Error) {
        if (err.message.startsWith('HTTP 401')) {
          setError(isEditMode
            ? 'You must be signed in to edit categories. Please sign in and try again.'
            : 'You must be signed in to create categories. Please sign in and try again.');
        } else if (err.message.startsWith('HTTP 403')) {
          setError('You do not have permission to manage categories. Admin role required.');
        } else if (err.message.includes('concurrency')) {
          setError('This category was modified by another user. Please refresh and try again.');
        } else if (err.message.includes('duplicate')) {
          setError('A category with this name already exists.');
        } else {
          setError(err.message);
        }
      } else {
        setError('An unexpected error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    form.reset();
    setError(null);
    setValidationErrors([]);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? 'Edit Category' : 'Create Category'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <FormField
              name="name"
              control={form.control}
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>Name *</FormLabel>
                  <div className="relative">
                    <Input
                      {...field}
                      placeholder="Enter category name"
                      className={fieldState.error || validationErrors.length > 0 ? 'border-red-500' : ''}
                    />
                    {nameCheckLoading && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                    )}
                  </div>
                  <FormMessage />
                  {validationErrors.map((error, index) => (
                    <p key={index} className="text-xs font-medium text-red-600">
                      {error}
                    </p>
                  ))}
                </FormItem>
              )}
            />

            <FormField
              name="description"
              control={form.control}
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    {...field}
                    placeholder="Optional description for this category"
                    rows={3}
                    className={fieldState.error ? 'border-red-500' : ''}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              name="color"
              control={form.control}
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>Color *</FormLabel>
                  <div className="space-y-3">
                    <Input
                      {...field}
                      placeholder="#FF5733"
                      className={fieldState.error ? 'border-red-500' : ''}
                    />
                    <div className="grid grid-cols-5 gap-2">
                      {DEFAULT_COLORS.map((color) => (
                        <button
                          key={color}
                          type="button"
                          className={`w-8 h-8 rounded-full border-2 transition-all ${
                            field.value === color 
                              ? 'border-gray-800 scale-110' 
                              : 'border-gray-300 hover:scale-105'
                          }`}
                          style={{ backgroundColor: color }}
                          onClick={() => field.onChange(color)}
                        />
                      ))}
                    </div>
                  </div>
                  {watchedColor && !isValidHexColor && (
                    <p className="text-xs font-medium text-red-600">
                      Please enter a valid hex color like #FF5733
                    </p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading || validationErrors.length > 0 || nameCheckLoading || !isValidHexColor}
              >
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isEditMode ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};